import { extend } from "@dhx/ts-common/core";
import { create, el, inject } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { Timepicker, TimepickerEvents } from "@dhx/ts-timepicker";
import { linkButtonClasses } from "./helper";
import { locale, getFormattedDate, DateHelper } from "@dhx/ts-common/date";
import {
	CalendarEvents,
	ICalendar,
	ICalendarConfig,
	ICalendarWeek,
	ViewMode,
	ICalendarHandlersMap,
} from "./types";
import { IHandlers } from "@dhx/ts-common/types";
import { isIE } from "@dhx/ts-common/html";

export class Calendar extends View implements ICalendar {
	public events: IEventSystem<CalendarEvents, ICalendarHandlersMap>;
	public config: ICalendarConfig;

	private _selected: Date[] = [];
	private _currentDate: Date;
	private _currentViewMode: ViewMode;
	private _isSelectedInCurrentRange: boolean;

	private _handlers: IHandlers;

	private _timepicker: Timepicker;
	private _time: string;
	private _linkedCalendar: Calendar;

	constructor(container: HTMLElement | string, config: ICalendarConfig = {}) {
		super(
			container,
			extend(
				{
					weekStart: "sunday",
					thisMonthOnly: false,
					dateFormat: window && (window as any).dhx && (window as any).dhx.dateFormat,
					width: "250px",
				},
				config
			)
		);

		this.events = new EventSystem<CalendarEvents>();
		this.config.disabledDates = this.config.disabledDates || this.config.block; // TODO: remove suite_7.0
		this.config.mode = this.config.mode || this.config.view; // TODO: remove suite_7.0

		if (!this.config.dateFormat) {
			if (this.config.timePicker) {
				if (this.config.timeFormat === 12) {
					this.config.dateFormat = "%d/%m/%y %h:%i %A";
				} else {
					this.config.dateFormat = "%d/%m/%y %H:%i";
				}
			} else {
				this.config.dateFormat = "%d/%m/%y";
			}
		}
		if (this.config.value) {
			this._setSelected(this.config.value);
		}
		if (this.config.date) {
			this._currentDate = DateHelper.toDateObject(this.config.date, this.config.dateFormat);
		} else if (this._getSelected()) {
			this._currentDate = DateHelper.copy(this._getSelected());
		} else {
			this._currentDate = new Date();
		}
		switch (this.config.mode) {
			case "month":
				this._currentViewMode = "month";
				break;
			case "year":
				this._currentViewMode = "year";
				break;
			case "timepicker":
				this._currentViewMode = this.config.timePicker ? "timepicker" : "calendar";
				break;
			default:
				this._currentViewMode = "calendar";
		}

		this._initHandlers();

		if (this.config.timePicker) {
			this._timepicker = new Timepicker(null, {
				timeFormat: this.config.timeFormat,
				controls: true,
			});
			const initTime = this._getSelected() || new Date();
			this._timepicker.setValue(initTime);
			this._time = this._timepicker.getValue();

			this._timepicker.events.on(TimepickerEvents.afterClose, () => {
				this._timepicker.setValue(this._time);
				this.showDate(null, "calendar");
			});

			this._timepicker.events.on(TimepickerEvents.afterApply, () => {
				const { hour, minute, AM } = this._timepicker.getValue(true);
				const oldDate = this._getSelected();
				const newDate = DateHelper.withHoursAndMinutes(
					this._getSelected() || new Date(),
					hour,
					minute,
					AM
				);
				if (this.events.fire(CalendarEvents.beforeChange, [newDate, oldDate, true])) {
					this._selected[this._selected.length - 1] = newDate;
					this.events.fire(CalendarEvents.change, [newDate, oldDate, true]);
				}
				this._time = this._timepicker.getValue();
				this.showDate(null, "calendar");
			});
		}

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	setValue(value: Date | Date[] | string | string[]): boolean {
		if (!value || (value instanceof Array && value.length === 0)) {
			return false;
		}
		this._selected = [];
		const currentDate = value instanceof Array ? value[0] : value;
		const date = DateHelper.toDateObject(currentDate, this.config.dateFormat);
		const oldDate = DateHelper.copy(this._getSelected());

		if (!this.events.fire(CalendarEvents.beforeChange, [date, oldDate, false])) {
			return false;
		}

		this._setSelected(value);

		if (this._timepicker) {
			this._timepicker.setValue(date);
			this._time = this._timepicker.getValue();
		}

		this.showDate(this._getSelected());
		this.events.fire(CalendarEvents.change, [date, oldDate, false]);
		this.paint();
		return true;
	}
	getValue<T extends boolean = false>(asDateObject?: T): Date | string;
	getValue<T extends boolean = true>(asDateObject?: T): Date[] | string[];
	getValue(asDateObject = false): Date | Date[] | string | string[] {
		if (!this._selected[0]) {
			return "";
		}
		if (this.config.range) {
			return asDateObject
				? this._selected.map(date => DateHelper.copy(date))
				: this._selected.map(date => getFormattedDate(this.config.dateFormat, date));
		}
		return asDateObject
			? DateHelper.copy(this._selected[0])
			: getFormattedDate(this.config.dateFormat, this._selected[0]);
	}
	getCurrentMode(): ViewMode {
		return this._currentViewMode;
	}
	showDate(date?: Date, mode?: ViewMode): void {
		if (date) {
			this._currentDate = DateHelper.copy(date);
		}
		if (mode) {
			this._currentViewMode = mode;
		}
		this.paint();
	}
	destructor(): void {
		this._linkedCalendar && this._unlink();
		this._timepicker && this._timepicker.destructor();
		this.events && this.events.clear();

		this.config = this.events = null;
		this._uid = this._selected = this._currentDate = this._currentViewMode = this._handlers = this._timepicker = this._time = null;

		this.unmount();
	}
	clear(): void {
		const oldDate = this.getValue(true) as Date;
		if (this.config.timePicker) {
			this._timepicker.clear();
			this._time = this._timepicker.getValue();
		}
		this._selected = [];
		this.showDate(null, this.config.mode);
		this.events.fire(CalendarEvents.change, [this.getValue(true) as Date, oldDate, false]);
	}
	link(targetCalendar: Calendar): void {
		if (this._linkedCalendar) {
			this._unlink();
		}

		this._linkedCalendar = targetCalendar;

		const rawLowerDate = this.getValue(true);
		const rawUpperDate = targetCalendar.getValue(true);

		let lowerDate: Date | null = rawLowerDate && DateHelper.dayStart(rawLowerDate as Date);
		let upperDate: Date | null = rawUpperDate && DateHelper.dayStart(rawUpperDate as Date);

		const getRangeClass = (date: Date) => {
			if (DateHelper.isSameDay(upperDate, lowerDate)) {
				return null;
			}
			let positionInRange = "dhx_calendar-day--in-range";
			if (DateHelper.isSameDay(date, lowerDate)) {
				positionInRange += " dhx_calendar-day--first-date";
			}
			if (DateHelper.isSameDay(date, upperDate)) {
				positionInRange += " dhx_calendar-day--last-date";
			}
			return positionInRange;
		};
		const rangeMark = (date: Date) => {
			if (lowerDate && upperDate) {
				return date >= lowerDate && date <= upperDate && getRangeClass(date);
			}
		};
		if (!this.config.$rangeMark || !this._linkedCalendar.config.$rangeMark) {
			this.config.$rangeMark = this._linkedCalendar.config.$rangeMark = rangeMark;
		}
		if (!this.config.disabledDates || !this._linkedCalendar.config.disabledDates) {
			this.config.disabledDates = (date: Date) => {
				if (upperDate) {
					return date > upperDate;
				}
			};
			this._linkedCalendar.config.disabledDates = (date: Date) => {
				if (lowerDate) {
					return date < lowerDate;
				}
			};
		}
		this.config.thisMonthOnly = true;
		targetCalendar.config.thisMonthOnly = true;

		this.events.on(
			CalendarEvents.change,
			date => {
				lowerDate = date ? DateHelper.dayStart(date) : null;
				this._linkedCalendar.paint();
			},
			"link"
		);

		this._linkedCalendar.events.on(
			CalendarEvents.change,
			date => {
				upperDate = date ? DateHelper.dayStart(date) : null;
				this.paint();
			},
			"link"
		);

		this._linkedCalendar.paint();
		this.paint();
	}
	private _unlink(): void {
		if (this._linkedCalendar) {
			this.config.$rangeMark = this._linkedCalendar.config.$rangeMark = null;
			this.config.disabledDates = this._linkedCalendar.config.disabledDates = null;

			this.events.detach(CalendarEvents.change, "link");
			this._linkedCalendar.events.detach(CalendarEvents.change, "link");
			this._linkedCalendar.paint();

			this._linkedCalendar = null;
		}
	}
	private _setSelected(value: Date | Date[] | string | string[]): void {
		const currentDate = value instanceof Array ? value[0] : value;
		const date = DateHelper.toDateObject(currentDate, this.config.dateFormat);

		if (value instanceof Array && this.config.range) {
			const filterDate: Date[] = [];
			value.forEach((element, index) => {
				if (index < 2) {
					filterDate.push(DateHelper.toDateObject(element, this.config.dateFormat));
				}
			});
			if (filterDate.length === 2 && filterDate[0] < filterDate[1]) {
				filterDate.forEach(element => this._selected.push(element));
			} else {
				this._selected[0] = filterDate[0];
			}
		} else {
			this._selected[0] = date;
		}
	}
	private _getSelected(): Date {
		return this._selected[this._selected.length - 1];
	}
	private _draw() {
		switch (this._currentViewMode) {
			case "calendar":
				this.events.fire(CalendarEvents.modeChange, ["calendar"]);
				return this._drawCalendar();
			case "month":
				this.events.fire(CalendarEvents.modeChange, ["month"]);
				return this._drawMonthSelector();
			case "year":
				this.events.fire(CalendarEvents.modeChange, ["year"]);
				return this._drawYearSelector();
			case "timepicker":
				this.events.fire(CalendarEvents.modeChange, ["timepicker"]);
				return this._drawTimepicker();
		}
	}
	private _initHandlers() {
		const ie_key_map = {
			Up: "ArrowUp",
			Down: "ArrowDown",
			Right: "ArrowRight",
			Left: "ArrowLeft",
			Esc: "Escape",
			Spacebar: "Space",
		};

		const getKey = e => {
			let key;
			if ((e.which >= 48 && e.which <= 57) || (e.which >= 65 && e.which <= 90)) {
				key = String.fromCharCode(e.which);
			} else {
				const keyName = e.which === 32 ? e.code : e.key;
				key = isIE() ? ie_key_map[keyName] || keyName : keyName;
			}

			return key;
		};

		const getVerticalRange = (decrease = false): number => {
			let range = 0;
			switch (this._currentViewMode) {
				case "calendar":
					range = decrease ? -7 : 7;
					break;
				case "month":
					range = decrease ? -4 : 4;
					break;
				case "year":
					range = decrease ? -4 : 4;
			}

			return range;
		};

		this._handlers = {
			onkeydown: {
				".dhx_calendar-year, .dhx_calendar-month, .dhx_calendar-day": (_e, vn) => {
					switch (getKey(_e)) {
						case "Enter":
							this._selectDate(_e, vn);
							break;
						case "ArrowLeft":
							this._moveBrowseFocus(_e, vn, -1);
							break;
						case "ArrowRight":
							this._moveBrowseFocus(_e, vn, 1);
							break;
						case "ArrowUp":
							this._moveBrowseFocus(_e, vn, getVerticalRange(true));
							break;
						case "ArrowDown":
							this._moveBrowseFocus(_e, vn, getVerticalRange());
							break;
					}
				},
			},
			onclick: {
				".dhx_calendar-year, .dhx_calendar-month, .dhx_calendar-day": (_e, vn) => {
					this._selectDate(_e, vn);
				},
				".dhx_calendar-action__cancel": () => {
					this.showDate(this._getSelected(), "calendar");
					this.events.fire(CalendarEvents.cancelClick, []);
				},
				".dhx_calendar-action__show-month": () => this.showDate(null, "month"),
				".dhx_calendar-action__show-year": () => this.showDate(null, "year"),
				".dhx_calendar-action__next": () => {
					let newDate;
					switch (this._currentViewMode) {
						case "calendar":
							newDate = DateHelper.addMonth(this._currentDate, 1);
							break;
						case "month":
							newDate = DateHelper.addYear(this._currentDate, 1);
							break;
						case "year":
							newDate = DateHelper.addYear(this._currentDate, 12);
					}
					this.showDate(newDate);
				},
				".dhx_calendar-action__prev": () => {
					let newDate;
					switch (this._currentViewMode) {
						case "calendar":
							newDate = DateHelper.addMonth(this._currentDate, -1);
							break;
						case "month":
							newDate = DateHelper.addYear(this._currentDate, -1);
							break;
						case "year":
							newDate = DateHelper.addYear(this._currentDate, -12);
					}
					this.showDate(newDate);
				},
				".dhx_calendar-action__show-timepicker": () => {
					this._currentViewMode = "timepicker";
					this.paint();
				},
			},
			onmouseover: {
				".dhx_calendar-day": (event, node) => {
					this.events.fire(CalendarEvents.dateMouseOver, [new Date(node.attrs._date), event]);
					this.events.fire(CalendarEvents.dateHover, [new Date(node.attrs._date), event]); // TODO: remove suite_7.0
				},
			},
		};
	}
	private _getData(date: Date): ICalendarWeek[] {
		this._isSelectedInCurrentRange = false;
		let firstDay;
		switch (this.config.weekStart) {
			case "saturday":
				firstDay = -1;
				break;
			case "monday":
				firstDay = 1;
				break;
			default:
				firstDay = 0;
		}
		const first = DateHelper.weekStart(DateHelper.monthStart(date), firstDay);
		const data = [];

		let weeksCount = 6;
		let currentDate = first;

		while (weeksCount--) {
			const currentWeek = DateHelper.getWeekNumber(currentDate);
			let disabledDays = 0;
			let daysCount = 7;
			const days = [];

			while (daysCount--) {
				const isDateWeekEnd = DateHelper.isWeekEnd(currentDate);
				const isCurrentMonth = date.getMonth() === currentDate.getMonth();
				const isBlocked = this.config.disabledDates && this.config.disabledDates(currentDate);

				const css = [];

				if (this.config.range && this._selected[0] && this._selected[1]) {
					const getRangeClass = () => {
						if (DateHelper.isSameDay(this._selected[0], this._selected[1])) {
							return null;
						}
						return "dhx_calendar-day--in-range";
					};
					const rangeMark = () => {
						if (this._selected[0] && this._selected[1]) {
							const firstDate = DateHelper.dayStart(this._selected[0]);
							const lastDate = DateHelper.dayStart(this._selected[1]);
							return currentDate >= firstDate && currentDate <= lastDate && getRangeClass();
						}
					};

					this.config.$rangeMark = rangeMark;
				}
				if (isDateWeekEnd && isCurrentMonth) {
					css.push("dhx_calendar-day--weekend");
				}
				if (!isCurrentMonth) {
					if (this.config.thisMonthOnly) {
						disabledDays++;
						css.push("dhx_calendar-day--hidden");
					} else {
						css.push("dhx_calendar-day--muffled");
					}
				}
				if (this.config.mark) {
					const markedCss = this.config.mark(currentDate);
					if (markedCss) {
						css.push(markedCss);
					}
				}
				if (this.config.$rangeMark) {
					const rangeMark = this.config.$rangeMark(currentDate);
					if (rangeMark) {
						css.push(rangeMark);
					}
				}
				if (isBlocked) {
					if (isDateWeekEnd) {
						css.push("dhx_calendar-day--weekend-disabled");
					} else {
						css.push("dhx_calendar-day--disabled");
					}
				}
				this._selected.forEach((selected, index) => {
					if (selected && DateHelper.isSameDay(selected, currentDate)) {
						this._isSelectedInCurrentRange = true;
						let dayCss = "dhx_calendar-day--selected";
						if (this.config.range) {
							dayCss += ` dhx_calendar-day--selected-${index === 0 ? "first " : "last"}`;
						}
						css.push(dayCss);
					}
				});
				days.push({
					date: currentDate,
					day: currentDate.getDate(),
					css: css.join(" "),
				});
				currentDate = DateHelper.addDay(currentDate);
			}
			data.push({
				weekNumber: currentWeek,
				days,
				disabledWeekNumber: disabledDays === 7,
			});
		}
		return data;
	}
	private _drawCalendar() {
		const date = this._currentDate;
		const { weekStart, thisMonthOnly, css, timePicker, width } = this.config;

		let weekDays;
		switch (weekStart) {
			case "saturday":
				weekDays = [locale.daysShort[6], ...locale.daysShort.slice(0, -1)];
				break;
			case "monday":
				weekDays = [...locale.daysShort.slice(1), locale.daysShort[0]];
				break;
			default:
				weekDays = locale.daysShort;
		}
		const weekDaysHeader = weekDays.map(day => el(".dhx_calendar-weekday", day));

		const data = this._getData(date);

		let isFirstItem = true;
		const selectedDate = this._getSelected();
		const isDateSelected = (date: Date) =>
			date && selectedDate && date.getTime() === selectedDate.getTime();
		const getCellAriaAttrs = item => {
			const attrs = {
				role: "button",
				tabindex: -1,
				"aria-pressed": "false",
			};

			if (item) {
				if (this._isSelectedInCurrentRange) {
					// it is correct that conditions are separated
					if (isDateSelected(item.date)) {
						attrs["tabindex"] = 0;
						attrs["aria-pressed"] = "true";
					}
				} else if (isFirstItem) {
					attrs["tabindex"] = 0;
				}

				isFirstItem = false;
			}

			return attrs;
		};

		let content = [];
		const weekNumbers = [];
		let weekNumbersWrapper;
		for (const week of data) {
			const weekRow = week.days.map(item =>
				el(
					"div.dhx_calendar-day",
					{
						class: item.css,
						_date: item.date,
						...getCellAriaAttrs(item),
					},
					item.day
				)
			);

			if (this.config.weekNumbers && !(week.disabledWeekNumber && thisMonthOnly)) {
				weekNumbers.push(
					el(
						"div",
						{
							class: "dhx_calendar-week-number",
						},
						week.weekNumber
					)
				);
			}
			content = content.concat(weekRow);
		}
		if (this.config.weekNumbers) {
			weekNumbersWrapper = el(".dhx_calendar__week-numbers", weekNumbers);
		}
		const widgetClass =
			"dhx_calendar dhx_widget" +
			(css ? " " + css : "") +
			(timePicker ? " dhx_calendar--with_timepicker" : "") +
			(this.config.weekNumbers ? " dhx_calendar--with_week-numbers" : "");
		return el(
			"div",
			{
				class: widgetClass,
				style: {
					width: this.config.weekNumbers ? parseInt(width.toString()) + 48 + "px" : width,
				},
				...this._handlers,
			},
			[
				el(".dhx_calendar__wrapper", [
					this._drawHeader(
						el(
							"button.dhx_calendar-action__show-month.dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
							{
								"aria-live": "polite",
								type: "button",
							},
							locale.months[date.getMonth()] + " " + date.getFullYear()
						)
					),
					this.config.weekNumbers &&
						el(".dhx_calendar__dates-wrapper", [
							el(".dhx_calendar__weekdays", weekDaysHeader),
							el(".dhx_calendar__days", content),
							weekNumbersWrapper,
						]),
					!this.config.weekNumbers && el(".dhx_calendar__weekdays", weekDaysHeader),
					!this.config.weekNumbers && el(".dhx_calendar__days", content),
					timePicker
						? el(".dhx_timepicker__actions", [
								el(
									"button.dhx_calendar__timepicker-button." +
										"dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--width_full.dhx_button--circle.dhx_calendar-action__show-timepicker",
									{ type: "button" },
									[
										el("span.dhx_button__icon.dxi.dxi-clock-outline"),
										el("span.dhx_button__text", this._time),
									]
								),
						  ])
						: null,
				]),
			]
		);
	}
	private _drawMonthSelector() {
		const date = this._currentDate;
		const currentMonth = date.getMonth();
		const currentYear = this._getSelected() ? this._getSelected().getFullYear() : null;
		const { css, timePicker, weekNumbers, width, mode } = this.config;
		const widgetClass =
			"dhx_calendar dhx_widget" +
			(css ? " " + css : "") +
			(timePicker ? " dhx_calendar--with_timepicker" : "") +
			(weekNumbers ? " dhx_calendar--with_week-numbers" : "");

		let isFirstItem = true;
		const isCurrentYear = currentYear === date.getFullYear();
		const isMonthSelected = i => isCurrentYear && currentMonth === i;
		const getCellAriaAttrs = (item, i) => {
			const attrs = {
				role: "button",
				tabindex: -1,
				"aria-pressed": "false",
			};

			if (item) {
				if (isCurrentYear) {
					// it is correct that conditions are separated
					if (isMonthSelected(i)) {
						attrs["tabindex"] = 0;
						attrs["aria-pressed"] = "true";
					}
				} else if (isFirstItem) {
					attrs["tabindex"] = 0;
				}

				isFirstItem = false;
			}

			return attrs;
		};

		return el(
			"div",
			{
				class: widgetClass,
				style: {
					width: weekNumbers ? parseInt(width.toString()) + 48 + "px" : width,
				},
				...this._handlers,
			},
			[
				el(".dhx_calendar__wrapper", [
					this._drawHeader(
						el(
							"button.dhx_calendar-action__show-year.dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
							{
								"aria-live": "polite",
								type: "button",
							},
							date.getFullYear()
						)
					),
					el(
						".dhx_calendar__months",
						locale.monthsShort.map((item, i) =>
							el(
								"div",
								{
									class:
										"dhx_calendar-month" +
										(isMonthSelected(i) ? " dhx_calendar-month--selected" : ""),
									...getCellAriaAttrs(item, i),
									_date: i,
								},
								item
							)
						)
					),
					mode !== "month"
						? el(".dhx_calendar__actions", [
								el(
									"button.dhx_button.dhx_button--color_primary.dhx_button--view_link.dhx_button--size_small.dhx_button--width_full.dhx_button--circle.dhx_calendar-action__cancel",
									{ type: "button" },
									locale.cancel
								),
						  ])
						: null,
				]),
			]
		);
	}
	private _drawYearSelector() {
		const date = this._currentDate;
		const yearsDiapason = DateHelper.getTwelweYears(date);
		const { css, timePicker, weekNumbers, width, mode } = this.config;
		const widgetClass =
			"dhx_calendar dhx_widget" +
			(css ? " " + css : "") +
			(timePicker ? " dhx_calendar--with_timepicker" : "") +
			(weekNumbers ? " dhx_calendar--with_week-numbers" : "");

		let isFirstItem = true;
		const isSelectedYearInRange =
			this._getSelected() && yearsDiapason.includes(this._getSelected().getFullYear());
		const isYearSelected = item => this._getSelected() && item === this._getSelected().getFullYear();
		const getCellAriaAttrs = item => {
			const attrs = {
				role: "button",
				tabindex: -1,
				"aria-pressed": "false",
			};

			if (item) {
				if (isSelectedYearInRange) {
					// it is correct that conditions are separated
					if (isYearSelected(item)) {
						attrs["tabindex"] = 0;
						attrs["aria-pressed"] = "true";
					}
				} else if (isFirstItem) {
					attrs["tabindex"] = 0;
				}

				isFirstItem = false;
			}

			return attrs;
		};

		return el(
			"div",
			{
				class: widgetClass,
				style: {
					width: weekNumbers ? parseInt(width.toString()) + 48 + "px" : width,
				},
				...this._handlers,
			},
			[
				el(".dhx_calendar__wrapper", [
					this._drawHeader(
						el(
							"button.dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
							{
								"aria-live": "polite",
								type: "button",
							},
							yearsDiapason[0] + "-" + yearsDiapason[yearsDiapason.length - 1]
						)
					),
					el(
						".dhx_calendar__years",
						yearsDiapason.map(item =>
							el(
								"div",
								{
									class:
										"dhx_calendar-year" +
										(isYearSelected(item) ? " dhx_calendar-year--selected" : ""),
									_date: item,
									...getCellAriaAttrs(item),
								},
								item
							)
						)
					),
					mode !== "year" && mode !== "month"
						? el(".dhx_calendar__actions", [
								el(
									"button.dhx_button.dhx_button--color_primary.dhx_button--view_link.dhx_button--size_small.dhx_button--width_full.dhx_button--circle.dhx_calendar-action__cancel",
									{ type: "button" },
									locale.cancel
								),
						  ])
						: null,
				]),
			]
		);
	}
	private _drawHeader(actionContent) {
		return el(".dhx_calendar__navigation", [
			el(
				"button.dhx_calendar-navigation__button.dhx_calendar-action__prev" +
					linkButtonClasses +
					".dhx_button--icon.dhx_button--circle",
				{
					"aria-label": "prev",
					type: "button",
				},
				[el(".dhx_button__icon.dxi.dxi-chevron-left")]
			),
			actionContent,
			el(
				"button.dhx_calendar-navigation__button.dhx_calendar-action__next" +
					linkButtonClasses +
					".dhx_button--icon.dhx_button--circle",
				{
					"aria-label": "next",
					type: "button",
				},
				[el(".dhx_button__icon.dxi.dxi-chevron-right")]
			),
		]);
	}
	private _drawTimepicker() {
		const { css, weekNumbers, width } = this.config;
		return el(
			".dhx_widget.dhx-calendar",
			{
				class: css ? " " + css : "",
				style: {
					width: weekNumbers ? parseInt(width.toString()) + 48 + "px" : width,
				},
			},
			[inject(this._timepicker.getRootView())]
		);
	}
	private _selectDate(_e, vn) {
		const date = vn.attrs._date;
		const oldDate = DateHelper.copy(this._getSelected());
		switch (this._currentViewMode) {
			case "calendar": {
				const mergedDate = this.config.timePicker
					? DateHelper.mergeHoursAndMinutes(date, this._getSelected() || this._currentDate)
					: date;
				if (!this.events.fire(CalendarEvents.beforeChange, [mergedDate, oldDate, true])) {
					return;
				}
				if (this.config.range && this._selected.length === 1 && this._selected[0] < mergedDate) {
					this._selected.push(mergedDate);
				} else {
					this._selected = [];
					this._selected[0] = mergedDate;
				}
				vn.el.blur();
				this.showDate(this._getSelected());
				this.events.fire(CalendarEvents.change, [date, oldDate, true]);
				break;
			}
			case "month":
				if (this.config.mode !== "month") {
					DateHelper.setMonth(this._currentDate, date);
					this.showDate(null, "calendar");
					this.events.fire(CalendarEvents.monthSelected, [date]);
				} else {
					const newDate = DateHelper.fromYearAndMonth(
						this._currentDate.getFullYear() || this._getSelected().getFullYear(),
						date
					);
					if (!this.events.fire(CalendarEvents.beforeChange, [newDate, oldDate, true])) {
						return;
					}
					this._currentDate = newDate;
					this._selected[0] = newDate;
					this.events.fire(CalendarEvents.change, [this._getSelected(), oldDate, true]);
					this.events.fire(CalendarEvents.monthSelected, [date]);
					this.paint();
				}
				break;
			case "year":
				if (this.config.mode !== "year") {
					DateHelper.setYear(this._currentDate, date);
					this.showDate(null, "month");
					this.events.fire(CalendarEvents.yearSelected, [date]);
				} else {
					const newDate = DateHelper.fromYear(date);
					if (!this.events.fire(CalendarEvents.beforeChange, [newDate, oldDate, true])) {
						return;
					}
					this._currentDate = newDate;
					this._selected[0] = newDate;
					this.events.fire(CalendarEvents.change, [this._getSelected(), oldDate, true]);
					this.events.fire(CalendarEvents.yearSelected, [date]);
					this.paint();
				}
		}
	}

	private _moveBrowseFocus(e, node, range: number) {
		if (node) {
			const nextNode = node.parent.body[node.idx + range];
			if (nextNode) {
				const $nextNode = nextNode.el;
				if ($nextNode) {
					e.target.tabIndex = -1;
					$nextNode.tabIndex = 0;
					$nextNode.focus({ preventScroll: true });
				}
			}
		}
	}
}
